"use client";

import { useState } from "react";
import { ArrowLeft, MapPin } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useStoreLocation } from "@/context/store-location-context";
import { CITIES_DATA, type CityData } from "@/constants/cities";
import { cn } from "@/lib/utils";
import { trackAction } from "@/lib/track-action";

type CitySelectionDialogProps = {
  open: boolean;
  onCitySelected: () => void;
  /** When provided, shows a back button (e.g. to return to login when user is a guest) */
  onBack?: () => void;
};

function CitySelectionContent({ onCitySelected }: { onCitySelected: () => void }) {
  const { setStoreLocation } = useStoreLocation();
  const [loadingCity, setLoadingCity] = useState<string | null>(null);

  const handleSelect = async (city: CityData) => {
    setLoadingCity(city.city);
    await setStoreLocation({ city: city.city, microhubId: city.defaultMicrohubId });
    trackAction("SELECT-CITY", city.city);
    setLoadingCity(null);
    onCitySelected();
  };

  return (
    <div className="grid grid-cols-1 gap-2 mt-2">
      {CITIES_DATA.map((city) => (
        <button
          key={city.displayName}
          type="button"
          disabled={loadingCity !== null}
          onClick={() => handleSelect(city)}
          className={cn(
            "flex items-center gap-3 w-full rounded-lg border px-4 py-3 text-left transition-colors cursor-pointer",
            "hover:bg-teal-50 hover:border-teal-400",
            loadingCity === city.city && "opacity-50"
          )}
        >
          <MapPin className="h-4 w-4 text-teal-500 flex-shrink-0" />
          <span className="font-medium">{city.displayName}</span>
        </button>
      ))}
    </div>
  );
}

export default function CitySelectionDialog({ open, onCitySelected, onBack }: CitySelectionDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const preventClose = (e: Event) => e.preventDefault();

  const header = (
    <>
      <div className="flex items-center gap-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="shrink-0 rounded-sm opacity-70 hover:opacity-100 transition-opacity cursor-pointer"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        )}
        <DialogTitle>Seleccionar ciudad</DialogTitle>
      </div>
      <DialogDescription>
        Selecciona tu ciudad para ver los productos disponibles en tu zona.
      </DialogDescription>
    </>
  );

  if (isDesktop || true) {
    return (
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent
          showCloseButton={false}
          onPointerDownOutside={preventClose}
          onEscapeKeyDown={preventClose}
          className="sm:max-w-sm"
        >
          <DialogHeader>{header}</DialogHeader>
          <CitySelectionContent onCitySelected={onCitySelected} />
        </DialogContent>
      </Dialog>
    );
  }

  // return (
  //   <Drawer open={open} onOpenChange={() => {}} dismissible={false}>
  //     <DrawerContent>
  //       <DrawerHeader>{header}</DrawerHeader>
  //       <div className="px-4 pb-8">
  //         <CitySelectionContent onCitySelected={onCitySelected} />
  //       </div>
  //     </DrawerContent>
  //   </Drawer>
  // );
}
